import { KeyboardArrowLeft } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import { Backdrop, Button, CircularProgress, Dialog } from "@mui/material";
import { PaymentMethod, Stripe } from "@stripe/stripe-js";
import cx from "classnames";
import React, { useState } from "react";
import { toast } from "react-toastify";

import { ReactComponent as XMark } from "assets/x-mark.svg";
import Card from "components/Payment/UpgradeFlowComponents/Card";
import ManagePaymentMethodsModal from "components/Payment/UpgradeFlowComponents/ManagePaymentMethodsModal";
import invoiceStyles from "components/Payment/UpgradeFlowComponents/ReviewPurchase.module.scss";
import { Invoice } from "models/Payment";
import { Subscription } from "models/User";
import { retryAutopilotPayment } from "services/AutopilotService";
import { getInvoices } from "services/SubscriptionService";
import { formatPrice, formatTimestamp } from "utils/helper";

import styles from "../ProfilePage.module.scss";

interface FailedInvoice {
  id: string;
  amount: number;
  currency: string;
}

interface FailedPaymentPanelProps {
  subscription: Subscription;
  invoice: FailedInvoice;
  stripe: Stripe;
  paymentMethods: PaymentMethod[];
  defaultMethod: PaymentMethod | null;
  onBack: () => void;
  onPaid: () => void;
  onMethodsChanged: () => void;
}

const FailedPaymentPanel: React.FC<FailedPaymentPanelProps> = ({
  subscription,
  invoice,
  stripe,
  paymentMethods,
  defaultMethod,
  onBack,
  onPaid,
  onMethodsChanged,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    defaultMethod || paymentMethods[0] || null,
  );
  const [processing, setProcessing] = useState(false);
  const [showManageMethods, setShowManageMethods] = useState(false);
  const [paidInvoice, setPaidInvoice] = useState<Invoice | null>(null);

  const amount = formatPrice({
    currency: invoice.currency,
    unit_amount_decimal: invoice.amount,
  });

  const handlePay = async () => {
    if (!selectedMethod) return;
    setProcessing(true);
    try {
      const data = await retryAutopilotPayment(selectedMethod.id);
      const { error } = await stripe.confirmPayment({
        clientSecret: data.payment_intent_secret,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (error) {
        toast.error(`Payment failed: ${error.message}`);
      } else {
        const invoiceData: Invoice = await getInvoices(
          subscription.id,
          data.invoice_id,
        );
        setPaidInvoice(invoiceData);
      }
    } catch (err) {
      const detail = (err as { detail?: string } | undefined)?.detail;
      toast.error(detail || "Something went wrong");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={styles.billingTabRoot}>
      <div className={styles.developerUsageSection}>
        <div className={styles.developerBack} onClick={onBack}>
          <KeyboardArrowLeft fontSize="small" />
          <span>Back</span>
        </div>

        <h5>Complete your payment</h5>
        <p className={styles.developerEmptyText}>
          Your last Autopilot payment of <b>{amount}</b> failed. Choose a
          payment method to settle it and resume API access.
        </p>

        {paymentMethods.length === 0 ? (
          <p className={styles.developerEmptyText}>
            No payment method added yet. Add one to continue.
          </p>
        ) : (
          paymentMethods.map((paymentMethod) => (
            <Card
              key={paymentMethod.id}
              paymentMethod={paymentMethod}
              onClick={(pm) => setSelectedMethod(pm)}
              isDefault={paymentMethod.id === defaultMethod?.id}
              isSelected={paymentMethod.id === selectedMethod?.id}
              onRemove={onMethodsChanged}
              hideOptions
              subscription={subscription}
            />
          ))
        )}

        <div
          className={styles.developerLink}
          onClick={() => setShowManageMethods(true)}
        >
          Manage payment methods
        </div>

        <div className={styles.developerPayActions}>
          <Button
            className="slapShotButton"
            disabled={processing || !selectedMethod}
            onClick={handlePay}
          >
            {processing ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              `Pay ${amount}`
            )}
          </Button>
        </div>
      </div>

      {showManageMethods && (
        <ManagePaymentMethodsModal
          onBack={() => {
            setShowManageMethods(false);
            onMethodsChanged();
          }}
          paymentMethods={paymentMethods}
          defaultMethod={defaultMethod}
          fetchPaymentMethods={onMethodsChanged}
          subscription={subscription}
          embedded
        />
      )}

      <Backdrop open={!!paidInvoice} sx={{ zIndex: 1300 }}>
        <Dialog
          open={!!paidInvoice}
          sx={{ ".MuiDialog-paper": { backgroundColor: "transparent" } }}
        >
          {paidInvoice && (
            <div className={invoiceStyles.invoiceContainer}>
              <div className={invoiceStyles.invoiceHeader}>
                <div>Invoice Paid</div>

                <button onClick={onPaid} type="button" className="closeButton">
                  <XMark />
                </button>
              </div>

              <div className={invoiceStyles.invoiceBody}>
                <div className={invoiceStyles.amountDetails}>
                  <img className={invoiceStyles.done} src="/done.gif" alt="" />
                  <div
                    className={invoiceStyles.invoiceAmount}
                  >{`${formatPrice(paidInvoice.price)} ${paidInvoice.price.currency.toUpperCase()}`}</div>
                  <div className={invoiceStyles.viewDetails}>
                    View invoice and payment details
                  </div>
                </div>

                <div className={invoiceStyles.extraDetails}>
                  <div className={invoiceStyles.extraDetailRow}>
                    <div className={invoiceStyles.extraDetail}>
                      Invoice number
                    </div>
                    <div>{paidInvoice.number}</div>
                  </div>
                  <div className={invoiceStyles.extraDetailRow}>
                    <div className={invoiceStyles.extraDetail}>
                      Payment Date
                    </div>
                    <div>{formatTimestamp(paidInvoice.date)}</div>
                  </div>
                </div>
              </div>

              <div className={invoiceStyles.invoiceFooter}>
                <Button
                  className={cx(
                    invoiceStyles.slapShotButtonOutline,
                    invoiceStyles.invoiceBtn,
                  )}
                  onClick={() => window.open(paidInvoice.invoice_url)}
                >
                  Download Invoice
                </Button>

                {paidInvoice.receipt_url && (
                  <Button
                    onClick={() => {
                      if (paidInvoice.receipt_url)
                        window.open(paidInvoice.receipt_url);
                    }}
                    className={cx(
                      invoiceStyles.slapShotButton,
                      invoiceStyles.receiptBtn,
                    )}
                  >
                    <DownloadIcon
                      sx={{ fontSize: "16px", marginRight: "5px" }}
                    />
                    Download Receipt
                  </Button>
                )}
              </div>
            </div>
          )}
        </Dialog>
      </Backdrop>
    </div>
  );
};

export default FailedPaymentPanel;
